import { useState, useRef, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTranslation } from 'react-i18next';

interface LanguageSelectorProps {
  variant?: 'header' | 'admin';
  scrolled?: boolean;
}

export default function LanguageSelector({ variant = 'header', scrolled = false }: LanguageSelectorProps) {
  const { language, setLanguage } = useLanguage();
  const { t } = useTranslation('home');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const isAdmin = variant === 'admin';

  const labelEn = t('lang_english');
  const labelEs = t('lang_spanish');

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
          isAdmin
            ? 'px-3 py-2 bg-gray-100 hover:bg-gray-200 text-brand-dark text-sm'
            : `px-2.5 py-1.5 text-sm font-medium ${scrolled ? 'text-brand-dark hover:text-brand-mint' : 'text-white/90 hover:text-white'}`
        }`}
      >
        <div className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold bg-brand-mint text-white">
          {language === 'en' ? 'EN' : 'ES'}
        </div>
        <span className={isAdmin ? '' : (scrolled ? 'text-brand-dark' : 'text-white/90')}>
          {language === 'en' ? labelEn : labelEs}
        </span>
        <i className={`ri-arrow-down-s-line text-sm ${isAdmin ? 'text-brand-muted' : (scrolled ? 'text-brand-muted' : 'text-white/70')}`} />
      </button>

      {open && (
        <div className="absolute right-0 z-50 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden w-40 top-full mt-2">
          <button
            onClick={() => {
              setLanguage('en');
              setOpen(false);
            }}
            className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors cursor-pointer ${
              language === 'en' ? 'bg-brand-light text-brand-dark font-semibold' : 'text-brand-muted hover:bg-gray-50'
            }`}
          >
            <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold bg-brand-mint text-white">EN</span>
            {labelEn}
          </button>
          <button
            onClick={() => {
              setLanguage('es');
              setOpen(false);
            }}
            className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors cursor-pointer ${
              language === 'es' ? 'bg-brand-light text-brand-dark font-semibold' : 'text-brand-muted hover:bg-gray-50'
            }`}
          >
            <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold bg-brand-mint text-white">ES</span>
            {labelEs}
          </button>
        </div>
      )}
    </div>
  );
}